import { SortDirection } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import SortChevronDown from '@/assets/icons/sort-chevron-down.svg';
import SortChevronUp from '@/assets/icons/sort-chevron-up.svg';
import { cn } from '@/utils/classnames';

import { Button } from './button';

interface SortColumnButtonProps extends React.PropsWithChildren {
  onSort: () => void;
  /** Current sort state from TanStack `column.getIsSorted()`. */
  sorted?: false | SortDirection;
}

function SortChevronsIcon({
  sorted = false,
}: {
  sorted?: false | SortDirection;
}) {
  return (
    <span className="inline-flex flex-col items-center gap-0.5" aria-hidden>
      <img
        src={SortChevronUp}
        alt=""
        data-testid="sort-chevron-up"
        className={cn('h-1 w-1.5', sorted === 'asc' && 'brightness-0')}
      />
      <img
        src={SortChevronDown}
        alt=""
        data-testid="sort-chevron-down"
        className={cn('h-1 w-1.5', sorted === 'desc' && 'brightness-0')}
      />
    </span>
  );
}

export function SortColumnButton({
  onSort,
  sorted = false,
  children,
}: SortColumnButtonProps) {
  const intl = useIntl();
  const isSorted = sorted === 'asc' || sorted === 'desc';

  const sortStateLabel =
    sorted === 'asc'
      ? intl.formatMessage({
          defaultMessage: 'sorted ascending',
          description:
            'Accessible suffix on a table column header that is sorted A→Z / low→high',
        })
      : sorted === 'desc'
        ? intl.formatMessage({
            defaultMessage: 'sorted descending',
            description:
              'Accessible suffix on a table column header that is sorted Z→A / high→low',
          })
        : null;

  return (
    <Button
      variant="ghost"
      onClick={onSort}
      className={cn(
        'text-body !p-0 font-normal hover:bg-transparent',
        isSorted
          ? 'text-fx-neutral-900 hover:text-fx-neutral-900 font-medium'
          : 'text-fx-neutral-600 hover:text-fx-neutral-900',
      )}
    >
      {children}
      <SortChevronsIcon sorted={sorted} />
      {sortStateLabel ? (
        <span className="sr-only">{sortStateLabel}</span>
      ) : null}
    </Button>
  );
}
